import { computed, DestroyRef, inject, Injectable, signal } from '@angular/core';
import { finalize, map, tap } from 'rxjs/operators';
import { ApiService } from '@core';
import { GENDERS, idNamesToOptions, normalizeArrayToObject, Option, PRODUCT_LIST_SORTING_OPTIONS } from '@models';
import { Observable } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FilterControlName,
  FilterOptions,
  FilterOptionsResponse,
  IFilterOptionsDataSource,
} from './product-filter.model';

@Injectable({
  providedIn: 'root',
})
export class FilterOptionsDataSourceService implements IFilterOptionsDataSource {
  private url = 'filters';
  private readonly apiService = inject(ApiService);
  private destroyRef = inject(DestroyRef);
  activeFilters = signal<FilterControlName[]>(['brandIds', 'category', 'sortBy', 'isAvailablePointsRange']);

  categories = signal<Option[]>([]);
  brands = signal<Option[]>([]);
  categoriesMap = signal<Record<string, any>>({});
  brandsMap = signal<Record<string, any>>({});
  genders = signal<Option[]>(GENDERS.map((gender) => ({ label: gender, value: gender })));
  sortBy = signal<Option[]>(PRODUCT_LIST_SORTING_OPTIONS);
  isCategoriesLoading = signal<boolean>(false);
  isBrandsLoading = signal<boolean>(false);
  isGendersLoading = signal<boolean>(false);
  isLoading = computed(() => this.isCategoriesLoading() || this.isBrandsLoading() || this.isGendersLoading());
  initialLoading = signal<boolean>(true);

  private isPromo = signal<boolean>(false);
  private seletectedCategories = signal<number[] | string[]>([]);
  private selectedBrands = signal<number[] | string[]>([]);
  private keyword = signal<string>('');
  private selectedFilters = computed(() => ({
    ...(this.seletectedCategories().length && { categoryIds: this.seletectedCategories() }),
    ...(this.selectedBrands().length && { brandIds: this.selectedBrands() }),
    ...(this.keyword() && { keyword: this.keyword() }),
    ...(this.isPromo() && { isPromo: this.isPromo() }),
  }));

  setBrands(brands: number[] | string[]) {
    this.selectedBrands.set(brands);
  }

  setCategories(categories: number[] | string[]) {
    this.seletectedCategories.set(categories);
  }

  setFilters(filters: { brands: number[] | string[]; categories: number[] | string[] }) {
    this.setBrands(filters.brands);
    this.setCategories(filters.categories);
  }

  setKeyword(keyword: string) {
    this.keyword.set(keyword);
  }
  setIsPromo(isPromo: boolean) {
    this.isPromo.set(isPromo);
  }

  resetSelectedFilters() {
    this.seletectedCategories.set([]);
    this.selectedBrands.set([]);
  }

  fetchOptions(): Observable<FilterOptions> {
    this.isCategoriesLoading.set(true);
    this.isBrandsLoading.set(true);

    return this.apiService.get<FilterOptionsResponse>(this.url, this.selectedFilters()).pipe(
      map((response) => ({
        categories: idNamesToOptions(response.categories),
        brands: idNamesToOptions(response.brands),
      })),
      tap((filters) => {
        const { categories, brands } = filters;
        const normalizedBrands = brands.length === 1 ? [] : brands;
        this.categories.set(categories);
        this.brands.set(normalizedBrands);
        this.categoriesMap.update((prev) => ({
          ...prev,
          ...normalizeArrayToObject(categories, 'value'),
        }));
        this.brandsMap.update((prev) => ({
          ...prev,
          ...normalizeArrayToObject(normalizedBrands, 'value'),
        }));
      }),
      finalize(() => {
        this.isCategoriesLoading.set(false);
        this.isBrandsLoading.set(false);
        this.initialLoading.set(false);
      }),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
